import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import LocationInput from '../common/LocationInput';
import { brand } from '../../theme/colors';
import {
  Add,
  Delete,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';

const MATERIAL_CATEGORIES = [
  { id: 'cement', name: 'Cement & Concrete', icon: '🧱' },
  { id: 'bricks', name: 'Bricks & Blocks', icon: '🏗️' },
  { id: 'steel', name: 'Steel & Rebar', icon: '⚙️' },
  { id: 'paint', name: 'Paint & Finishes', icon: '🎨' },
  { id: 'roofing', name: 'Roofing Materials', icon: '🏠' },
  { id: 'timber', name: 'Timber', icon: '🪵' },
  { id: 'plumbing', name: 'Plumbing', icon: '🚰' },
  { id: 'electrical', name: 'Electrical', icon: '⚡' }
];

const UNITS = [
  'Bags', 'Pieces', 'Meters', 'Kilograms', 'Tons', 'Cubic Meters',
  'Heaps/Loads', 'Sheets', 'Rolls', 'Liters', 'Gallons', 'Other'
];

const MaterialRequestForm = ({ project, onNext, onBack, onCancel, initialItems = null }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [materialItems, setMaterialItems] = useState({}); // {categoryId: [items]}

  // Prefill from a caller-supplied map {categoryId: [{materialName, quantity, unit, notes}]}
  // (e.g. unpurchased BOQ items carried into the quote flow).
  React.useEffect(() => {
    if (!initialItems || Object.keys(initialItems).length === 0) return;
    const categoryIds = Object.keys(initialItems).filter(id => MATERIAL_CATEGORIES.some(c => c.id === id));
    if (categoryIds.length === 0) return;
    setSelectedCategories(categoryIds);
    const seeded = {};
    categoryIds.forEach(categoryId => {
      seeded[categoryId] = initialItems[categoryId].map((item, i) => ({
        id: Date.now() + i + Math.random(),
        materialName: item.materialName || '',
        quantity: item.quantity != null ? String(item.quantity) : '',
        unit: UNITS.includes(item.unit) ? item.unit : 'Other',
        specifications: item.specifications || '',
        brandPreference: '',
        notes: item.notes || ''
      }));
    });
    setMaterialItems(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [deliveryTimeframe, setDeliveryTimeframe] = useState('2_weeks');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState(
    project?.location ? (typeof project.location === 'string' ? { address: project.location } : project.location) : null
  );
  const [generalNotes, setGeneralNotes] = useState('');
  const [errors, setErrors] = useState({});

  const handleCategoryToggle = (category) => {
    if (selectedCategories.includes(category.id)) {
      // Remove category and its items
      setSelectedCategories(prev => prev.filter(c => c !== category.id));
      setMaterialItems(prev => {
        const newItems = { ...prev };
        delete newItems[category.id];
        return newItems;
      });
    } else {
      // Add category with one empty item
      setSelectedCategories(prev => [...prev, category.id]);
      setMaterialItems(prev => ({
        ...prev,
        [category.id]: [{
          id: Date.now(),
          materialName: '',
          quantity: '',
          unit: 'Bags',
          specifications: '',
          brandPreference: '',
          notes: ''
        }]
      }));
    }
  };

  const addItemToCategory = (categoryId) => {
    setMaterialItems(prev => ({
      ...prev,
      [categoryId]: [
        ...(prev[categoryId] || []),
        {
          id: Date.now(),
          materialName: '',
          quantity: '',
          unit: 'Bags',
          specifications: '',
          brandPreference: '',
          notes: ''
        }
      ]
    }));
  };

  const removeItemFromCategory = (categoryId, itemId) => {
    setMaterialItems(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter(item => item.id !== itemId)
    }));
  };

  const updateItem = (categoryId, itemId, field, value) => {
    setMaterialItems(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeCategory = (categoryId) => {
    setSelectedCategories(prev => prev.filter(c => c !== categoryId));
    setMaterialItems(prev => {
      const newItems = { ...prev };
      delete newItems[categoryId];
      return newItems;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (selectedCategories.length === 0) {
      newErrors.categories = 'Please select at least one material category';
    }

    // Validate items in each category
    for (const categoryId of selectedCategories) {
      const items = materialItems[categoryId] || [];
      if (items.length === 0) {
        newErrors[`category_${categoryId}`] = 'Please add at least one item to this category';
      }

      items.forEach((item, index) => {
        if (!item.materialName || !item.materialName.trim()) {
          newErrors[`item_${categoryId}_${index}_name`] = 'Material name is required';
        }
        if (!item.quantity || parseFloat(item.quantity) <= 0) {
          newErrors[`item_${categoryId}_${index}_quantity`] = 'Valid quantity is required';
        }
      });
    }

    if (deliveryTimeframe === 'specific' && !deliveryDate) {
      newErrors.deliveryDate = 'Please select a delivery date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateForm()) {
      return;
    }

    // Format material request data
    const materialRequest = selectedCategories.map(categoryId => {
      const category = MATERIAL_CATEGORIES.find(c => c.id === categoryId);
      return {
        categoryId,
        categoryName: category.name,
        items: materialItems[categoryId].map(item => ({
          materialName: item.materialName,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          specifications: item.specifications,
          brandPreference: item.brandPreference,
          notes: item.notes
        }))
      };
    });

    const formData = {
      requestType: 'material_form',
      materialRequest,
      materialCategories: selectedCategories.map(id => MATERIAL_CATEGORIES.find(c => c.id === id).name),
      deliveryAddress: typeof deliveryAddress === 'object' && deliveryAddress !== null
        ? {
            address: deliveryAddress.address || '',
            street: deliveryAddress.street || deliveryAddress.addressComponents?.route || '',
            suburb: deliveryAddress.suburb || deliveryAddress.addressComponents?.sublocality || '',
            city: deliveryAddress.city || deliveryAddress.addressComponents?.locality || '',
            province: deliveryAddress.province || deliveryAddress.addressComponents?.administrativeAreaLevel1 || '',
            postal_code: deliveryAddress.postalCode || deliveryAddress.addressComponents?.postalCode || '',
            country: deliveryAddress.country || deliveryAddress.addressComponents?.country || '',
            coordinates: deliveryAddress.coordinates || null
          }
        : {
            address: deliveryAddress || '',
            street: '',
            suburb: '',
            city: '',
            province: '',
            postal_code: '',
            country: ''
          },
      deliveryTimeframe,
      deliveryDate: deliveryTimeframe === 'specific' ? deliveryDate : null,
      specialRequirements: generalNotes,
      additionalNotes: generalNotes
    };

    onNext(formData);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Material Request Form
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBack && (
            <Button size="small" onClick={onBack} startIcon={<ArrowBack />}>
              Back
            </Button>
          )}
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {project && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Project: <strong>{project.title}</strong>
        </Typography>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Tell us what materials you need and we'll send it to suppliers for quotes.
      </Typography>

      {/* Category Selection */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Select Material Categories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the types of materials you need:
        </Typography>

        <Grid container spacing={2}>
          {MATERIAL_CATEGORIES.map((category) => (
            <Grid item xs={12} sm={6} md={3} key={category.id}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: selectedCategories.includes(category.id) ? 2 : 1,
                  borderColor: selectedCategories.includes(category.id) ? 'primary.main' : 'grey.300',
                  bgcolor: selectedCategories.includes(category.id) ? 'primary.light' : 'background.paper',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.light'
                  }
                }}
                onClick={() => handleCategoryToggle(category)}
              >
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {category.icon}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {category.name}
                </Typography>
                {selectedCategories.includes(category.id) && (
                  <Chip label="Selected" size="small" color="primary" sx={{ mt: 1 }} />
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {errors.categories && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.categories}
          </Alert>
        )}
      </Paper>

      {/* Material Items by Category */}
      {selectedCategories.map((categoryId) => {
        const category = MATERIAL_CATEGORIES.find(c => c.id === categoryId);
        const items = materialItems[categoryId] || [];

        return (
          <Paper key={categoryId} elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {category.icon} {category.name}
              </Typography>
              <Button
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => removeCategory(categoryId)}
              >
                Remove Category
              </Button>
            </Box>

            {items.map((item, index) => (
              <Box key={item.id} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">
                    Item {index + 1} of {items.length}
                  </Typography>
                  {items.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeItemFromCategory(categoryId, item.id)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Material/Product *"
                      value={item.materialName}
                      onChange={(e) => updateItem(categoryId, item.id, 'materialName', e.target.value)}
                      error={!!errors[`item_${categoryId}_${index}_name`]}
                      helperText={errors[`item_${categoryId}_${index}_name`]}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Quantity *"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(categoryId, item.id, 'quantity', e.target.value)}
                      error={!!errors[`item_${categoryId}_${index}_quantity`]}
                      helperText={errors[`item_${categoryId}_${index}_quantity`]}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Unit *</InputLabel>
                      <Select
                        value={item.unit}
                        label="Unit *"
                        onChange={(e) => updateItem(categoryId, item.id, 'unit', e.target.value)}
                      >
                        {UNITS.map(unit => (
                          <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Specifications/Brand Preference (Optional)"
                      value={item.specifications}
                      onChange={(e) => updateItem(categoryId, item.id, 'specifications', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Additional Notes (Optional)"
                      multiline
                      rows={2}
                      value={item.notes}
                      onChange={(e) => updateItem(categoryId, item.id, 'notes', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => addItemToCategory(categoryId)}
              sx={{ mt: 1 }}
            >
              Add Another Item to {category.name}
            </Button>

            {errors[`category_${categoryId}`] && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors[`category_${categoryId}`]}
              </Alert>
            )}
          </Paper>
        );
      })}

      {/* Delivery Details */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Delivery Details
        </Typography>

        <LocationInput
          label="Delivery Address"
          value={deliveryAddress}
          onChange={(location) => {
            setDeliveryAddress(location);
            setErrors(prev => ({ ...prev, deliveryAddress: '' }));
          }}
          placeholder="Enter delivery address..."
          required
          error={!!errors.deliveryAddress}
          helperText={errors.deliveryAddress}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>When do you need delivery?</InputLabel>
          <Select
            value={deliveryTimeframe}
            label="When do you need delivery?"
            onChange={(e) => setDeliveryTimeframe(e.target.value)}
          >
            <MenuItem value="asap">ASAP (1 week)</MenuItem>
            <MenuItem value="2_weeks">Within 2 weeks</MenuItem>
            <MenuItem value="1_month">Within 1 month</MenuItem>
            <MenuItem value="flexible">Flexible</MenuItem>
            <MenuItem value="specific">Specific date</MenuItem>
          </Select>
        </FormControl>

        {deliveryTimeframe === 'specific' && (
          <TextField
            type="date"
            fullWidth
            label="Delivery Date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!!errors.deliveryDate}
            helperText={errors.deliveryDate}
          />
        )}

        <TextField
          fullWidth
          multiline
          rows={3}
          label="General Notes/Special Requirements"
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* Summary */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f5f7fa' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          SUMMARY:
        </Typography>
        <Typography variant="body2">
          • {selectedCategories.length} Categories: {selectedCategories.map(id => MATERIAL_CATEGORIES.find(c => c.id === id).name).join(', ')}
        </Typography>
        <Typography variant="body2">
          • {Object.values(materialItems).reduce((sum, items) => sum + items.length, 0)} Total Items
        </Typography>
        <Typography variant="body2">
          • Delivery: {typeof deliveryAddress === 'object' && deliveryAddress !== null ? deliveryAddress.address : deliveryAddress || 'Not specified'} ({deliveryTimeframe === 'specific' ? deliveryDate : deliveryTimeframe})
        </Typography>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={onCancel || onBack} variant="outlined">
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => alert('Save draft coming soon')}>
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForward />}
            sx={{
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover
              }
            }}
          >
            Continue to Select Suppliers
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MaterialRequestForm;

